import type {Location} from 'react-router'
import isArray from 'lodash/isArray'
import isBoolean from 'lodash/isBoolean'
import isDate from 'lodash/isDate'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import {mmap} from '@freckle/maybe'

const ARRAY_VALUE_SPLIT = '|'

export function extractQueryParamsTuples(
  search: string,
): Array<[string, string]> | undefined | null {
  const cleanedSearch = decodeURIComponent(search).replace('?', '').trim()

  if (cleanedSearch.length === 0) {
    return null
  }

  const searchValues = cleanedSearch.split('&')

  return searchValues.map(queryItem => {
    const keyVal = queryItem.split('=')

    return [keyVal[0]!, keyVal[1]!]
  })
}

function extractHashQueryParamsTuples(
  location: Location,
): Array<[string, string]> | undefined | null {
  const {hash} = location
  if (hash.indexOf('?') < 0) {
    return null
  }
  const cleanedHash = decodeURIComponent(hash)
    .replace(/(^.*\?)/, '')
    .trim()

  if (cleanedHash.length === 0) {
    return null
  }

  const searchValues = cleanedHash.split('&')

  return searchValues.map(queryItem => {
    const keyVal = queryItem.split('=')

    return [keyVal[0]!, keyVal[1]!]
  })
}

export function extractQueryParams(location: Location): Map<string, string> | undefined | null {
  return mmap(t => new Map(t), extractQueryParamsTuples(location.search))
}

export function extractHashQueryParams(location: Location): Map<string, string> | undefined | null {
  return mmap(t => new Map(t), extractHashQueryParamsTuples(location))
}

export function parseQueryParamArray<T>(pathArray: string, callback: (arg: string) => T): Array<T> {
  const args = pathArray.split(ARRAY_VALUE_SPLIT)
  return args.filter(arg => arg.trim().length > 0).map(callback)
}

export function parseQueryParamNumArray(pathArray: string): Array<number> {
  return parseQueryParamArray(pathArray, a => parseInt(a, 10))
}

export function parseQueryParamTextArray(pathArray: string): Array<string> {
  return parseQueryParamArray(pathArray, a => a)
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toFragment(route: string, queryParams?: any | null): string {
  if (queryParams !== null && queryParams !== undefined) {
    const queryParamsStr = isString(queryParams) ? queryParams : toQueryString(queryParams)
    return queryParamsStr !== '' ? appendQueryStringToRoute(route, queryParamsStr) : route
  }
  return route
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function appendQueryStringToRoute(route: string, queryString: string | any): string {
  const divider = route.includes('?') ? '&' : '?'
  if (typeof queryString === 'string') {
    return `${route}${divider}${queryString}`
  } else {
    return `${route}${divider}${toQueryString(queryString)}`
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toQueryString(params?: any | null, namePrefix: string = ''): string {
  if (params === null || params === undefined) {
    return ''
  }

  const queryParamsStrArr = Object.entries(params).map(([key, value]) => {
    const name = `${namePrefix}${key}`
    if (value === null || value === undefined) {
      return ''
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return `${name}=${encodeSplit(encodeURIComponent(String(value)))}`
    } else if (isArray(value)) {
      const arrStr = arrayToString(value)
      if (arrStr !== '') {
        return `${name}=${arrStr}`
      }
    } else {
      /* This is an object, we dig in */
      const valueNamePrefix = `${name}.`
      return toQueryString(value, valueNamePrefix)
    }
  })

  return queryParamsStrArr.filter(v => v !== '').join('&')
}

function isPrimitive(value: string | number | boolean | null | void): boolean {
  return isString(value) || isNumber(value) || isBoolean(value) || isDate(value)
}

export function arrayToString(arrayParam: Array<string | number | boolean | null | void>): string {
  /* For arrays, we use the ARRAY_VALUE_SPLIT between values
   * This function expects values to be primitives, it throws an error on
   * non-primitives values
   */
  return arrayParam.reduce((acc: string, arrParam) => {
    if (arrParam !== null && arrParam !== undefined) {
      if (isPrimitive(arrParam)) {
        acc += `${ARRAY_VALUE_SPLIT}${encodeSplit(encodeURIComponent(String(arrParam)))}`
      } else {
        throw new Error('Non-primitive values in array')
      }
    }
    return acc
  }, '')
}

function encodeSplit(value: string): string {
  return String(value).replace(ARRAY_VALUE_SPLIT, encodeURIComponent(ARRAY_VALUE_SPLIT))
}
